import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentSiteId } from '@hotjar/site';
import { useStickyNotification } from '@hotjar/navigation';
import { SessionStorage } from '@hotjar/insights-common/lib/storage-factory';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SITE } from '@hotjar/webapp/constants/routes';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { useSamplingState } from '../../hooks/useSamplingState';
import { useTrafficCoverage } from '../../hooks/useTrafficCoverage';

function getSessionSettingsRoute(siteId: SiteId) {
  return getRoute(SITE.SETTINGS.path, {
    siteId,
  });
}

const getCoverageUpsellKey = (siteId?: string | null) => `coverageUpsellAlertDismissed-${siteId}`;

// Show a coverage upsell alert when the coverage is below 100% and the client script is enabled without any issues
// Meaning when useActivationAlerts doesn't show anything.
// The alert is only visible in the view where the hook is used.
export const useCoverageUpsellAlert = (
  track: {
    page: string;
    trackViewElement: (
      eventName: string,
      uiPosition: string,
      additionalData: Record<string, string>,
    ) => void;
    trackClick: (
      eventName: string,
      uiPosition: string,
      additionalData: Record<string, string>,
    ) => void;
  },
  openUpsellModal: (referrer: KnownReferrer) => void,
  shouldBeHidden?: boolean,
): void => {
  const { show, hide } = useStickyNotification();
  const { t } = useTranslation('common');
  const currentSiteId = useCurrentSiteId();
  const { isLoading: trafficCoverageLoading, coveragePercentage } = useTrafficCoverage();
  const { inDisabledState: sessionCaptureDisabled } = useSamplingState();
  const trackingCodeState = useTrackingCodeState();

  const [isDismissed, setIsDismissed] = useState(
    currentSiteId ? SessionStorage.get(getCoverageUpsellKey(currentSiteId), false) : false,
  );

  useEffect(() => {
    if (currentSiteId) {
      setIsDismissed(SessionStorage.get(getCoverageUpsellKey(currentSiteId), false));
    }
  }, [currentSiteId]);

  const shouldRender = useMemo(() => {
    if (
      shouldBeHidden ||
      !currentSiteId ||
      !track.trackViewElement ||
      !track.trackClick ||
      isDismissed ||
      trafficCoverageLoading ||
      sessionCaptureDisabled ||
      !trackingCodeState?.isActive ||
      !trackingCodeState.installed
    ) {
      return false;
    }
    return (
      typeof coveragePercentage === 'number' && coveragePercentage >= 50 && coveragePercentage < 100
    );
  }, [
    shouldBeHidden,
    currentSiteId,
    coveragePercentage,
    isDismissed,
    sessionCaptureDisabled,
    track.trackClick,
    track.trackViewElement,
    trackingCodeState.installed,
    trackingCodeState?.isActive,
    trafficCoverageLoading,
  ]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    SessionStorage.set(getCoverageUpsellKey(currentSiteId), true);
    track.trackClick(
      'Traffic Coverage Upsell Dismiss',
      `${track.page}_top_banner_X_icon_to_dismiss`,
      {
        element_name: 'warning_sampled.data_dismissed',
      },
    );
  }, [currentSiteId, track.page, track.trackClick]);

  useEffect(() => {
    if (shouldRender) {
      track.trackViewElement('Traffic Coverage Upsell', `${track.page}_top_banner`, {
        element_name: 'warning_sampled.data_upgrade',
      });
      show('coverage-upsell', {
        isDismissible: true,
        kind: 'warning',
        actionText: t('CoverageUpsell.actionText'),
        actionHref: getSessionSettingsRoute(Number(currentSiteId)),
        onAction: () => openUpsellModal(`${track.page}_top_banner_upgrade_link` as KnownReferrer), // FIXME: remove type assertion
        onDismiss: handleDismiss,
        text: t('CoverageUpsell.statement'),
      });
    } else {
      hide('coverage-upsell');
    }

    return function cleanup() {
      hide('coverage-upsell');
    };
  }, [
    currentSiteId,
    track.page,
    handleDismiss,
    hide,
    openUpsellModal,
    shouldRender,
    show,
    t,
    track.trackViewElement,
  ]);
};
